import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, Users, Clock, CheckCircle2, ArrowRight, Building2, Globe, Zap, Sparkles } from 'lucide-react'

const whyChooseReasons = [
  {
    icon: Award,
    title: 'ISO Certified Excellence',
    description: 'Multiple ISO certifications ensuring quality, security, and environmental compliance in all deliverables.',
    highlight: '10 Certifications',
    color: '#6366f1',
  },
  {
    icon: Users,
    title: 'Expert Team',
    description: '50+ skilled professionals including project managers, engineers, and domain specialists.',
    highlight: '50+ Professionals',
    color: '#8b5cf6',
  },
  {
    icon: Clock,
    title: 'On-Time Delivery',
    description: 'Proven track record of completing projects within deadlines with full compliance to government norms.',
    highlight: '98% On-Time',
    color: '#ec4899',
  },
  {
    icon: Building2,
    title: 'Pan-India Presence',
    description: 'Serving clients across Maharashtra with dedicated project teams and local expertise.',
    highlight: '20+ Districts',
    color: '#10b981',
  },
]

const benefits = [
  'End-to-end project lifecycle management',
  'Deep government compliance expertise',
  'Transparent pricing and milestone billing',
  'Dedicated project support team',
  'Regular progress reporting and updates',
  'Quality assurance at every stage',
]

const keyDifferentiators = [
  { icon: Building2, label: 'Government Focus', value: '100%' },
  { icon: Globe, label: 'Pan-Maharashtra', value: '20+ Districts' },
  { icon: Zap, label: 'Response Time', value: '< 24 Hours' },
]

export const WhyChooseSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-24 relative">
      {/* Background is global in Home.tsx */}
      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
            }}
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-slate-300">Why Choose Us</span>
            <span className="text-sm font-bold text-primary">({whyChooseReasons.length})</span>
          </motion.div>

          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Your Trusted <span className="gradient-text">Partner</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl">
            We deliver excellence through innovation, integrity, and unwavering commitment to client success in every government project.
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            {/* Key Differentiators */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {keyDifferentiators.map((diff, index) => {
                const Icon = diff.icon
                return (
                  <motion.div
                    key={diff.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="text-center p-4 rounded-2xl"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                      style={{
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                      }}
                    >
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-xl font-bold text-white mb-1">{diff.value}</p>
                    <p className="text-xs text-slate-400">{diff.label}</p>
                  </motion.div>
                )
              })}
            </div>

            {/* Benefits List */}
            <div className="space-y-3 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 p-3 rounded-xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06) 0%, rgba(139, 92, 246, 0.04) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                  }}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
                    }}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                  </div>
                  <span className="text-sm text-slate-300">{benefit}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/about"
              className="btn-glow inline-flex items-center gap-2 px-6 py-3 text-white font-medium rounded-xl transition-all"
            >
              <span>Learn More About Us</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Right - Reason Cards */}
          <div className="space-y-4">
            {whyChooseReasons.map((reason, index) => {
              const Icon = reason.icon
              return (
                <motion.div
                  key={reason.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  {/* Number Badge */}
                  <div className="absolute -top-3 -left-3 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    style={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    }}
                  >
                    <span className="text-white">{index + 1}</span>
                  </div>

                  <div className="flex items-start gap-4 p-5 rounded-2xl transition-all"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: `${reason.color}20`,
                        border: `1px solid ${reason.color}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: reason.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-white">{reason.title}</h3>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full"
                          style={{
                            background: `${reason.color}20`,
                            color: reason.color,
                            border: `1px solid ${reason.color}40`,
                          }}
                        >
                          {reason.highlight}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{reason.description}</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}